// react
import { useState, useCallback } from 'react';

// components
import { BaseField, FieldProps } from '@/components/atoms/BaseField/BaseField';

// mui
import { TextField } from '@mui/material';

enum Err {
  InvalidCodePasted = 'The text you pasted is not a valid verification code - it should be exactly 6 numeric digits',
  InvalidCodeLength = 'The verification code must be 6 digits long',
}

const sanitizeValue = (value: string) => {
  return value
    .replace(/[^0-9]/g, '')
    .substring(0, 6)
    .trim();
};

interface VerificationCodeInputProps extends FieldProps<string> {
  autoFocus?: boolean;
}

export const VerificationCodeInput = ({
  id,
  ariaLabel,
  label,
  description,
  tooltip,
  isRequired,
  isHideTitle,
  value,
  onChange,
  showError,
  autoFocus,
}: VerificationCodeInputProps) => {
  const [isError, setIsError] = useState<boolean>(false);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange?.(sanitizeValue(event.target.value)),
    [value, onChange],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement | HTMLDivElement>) => {
      event.preventDefault();
      const clipboardData = event.clipboardData;

      // Check if there is text data in the clipboard
      if (!clipboardData.types.includes('text/plain')) {
        showError?.(Err.InvalidCodePasted);
      }
      const pastedText = clipboardData.getData('text/plain');
      onChange?.(sanitizeValue(clipboardData.getData('text/plain')));
    },
    [onChange, showError],
  );

  const handleBlur = useCallback(() => {
    if (!!value && value.length !== 0 && value.length < 6) {
      setIsError(true);
      showError?.(Err.InvalidCodeLength);
      return;
    }
    setIsError(false);
    onChange?.(value || '');
  }, [value, onChange, showError]);

  const handleFocus = useCallback(() => {
    setIsError(false);
  }, []);

  return (
    <BaseField
      id={id}
      ariaLabel={ariaLabel}
      description={description}
      tooltip={tooltip}
      isRequired={isRequired}
      isHideTitle={isHideTitle}
    >
      <TextField
        id='verification-code-input'
        aria-label='verification code'
        label={label}
        placeholder='●●●●●●'
        inputMode='numeric'
        type='tel'
        value={value}
        onChange={handleChange}
        onPaste={handlePaste}
        onFocus={handleFocus}
        onBlur={handleBlur}
        error={isError}
        helperText={isError ? Err.InvalidCodeLength : 'Enter the 6-digit code'}
        style={{ width: '100%' }}
        autoFocus={autoFocus}
      />
    </BaseField>
  );
};

VerificationCodeInput.displayName = 'VerificationCodeInput';
export default VerificationCodeInput;
